import { useEffect, useState } from 'react';
import Modal from '@/components/base/Modal';
import Button from '@/components/base/Button';
import Input from '@/components/base/Input';
import { resetUserPassword } from '@/lib/userService';
import { useToast } from '@/components/feature/ToastProvider';
import { useAuth } from '@/contexts/AuthContext';
import { logAudit } from '@/lib/audit';
import type { UserAccount } from '@/types';

interface ChangePasswordModalProps {
  user: UserAccount | null;
  onClose: () => void;
}

export default function ChangePasswordModal({ user, onClose }: ChangePasswordModalProps) {
  const { notify } = useToast();
  const { profile } = useAuth();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [show, setShow] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setPassword('');
    setConfirm('');
    setShow(false);
    setError(null);
  }, [user]);

  if (!user) return null;

  const handleSave = async () => {
    setError(null);
    if (password.length < 6) {
      setError('La contraseña debe tener al menos 6 caracteres.');
      return;
    }
    if (password !== confirm) {
      setError('Las contraseñas no coinciden.');
      return;
    }

    setSaving(true);
    const { error: serviceError } = await resetUserPassword(user.id, password);
    setSaving(false);

    if (serviceError) {
      setError(serviceError);
      return;
    }

    await logAudit({
      userId: profile?.id ?? null,
      userName: profile?.full_name || profile?.username || 'Usuario',
      action: 'CAMBIAR_CONTRASENA',
      module: 'Usuarios',
      recordId: user.id,
      description: `Contraseña actualizada para el usuario ${user.username}.`,
    });

    notify({
      tone: 'success',
      title: 'Contraseña actualizada',
      message: `Se cambió la contraseña de ${user.username}.`,
    });
    onClose();
  };

  return (
    <Modal
      open
      title="Cambiar contraseña"
      description={`Usuario: ${user.username}`}
      icon="ri-lock-password-line"
      onClose={onClose}
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Cancelar
          </Button>
          <Button icon="ri-save-line" onClick={handleSave} loading={saving}>
            Guardar contraseña
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Input
          label="Nueva contraseña"
          name="new_password"
          type={show ? 'text' : 'password'}
          icon="ri-lock-line"
          value={password}
          autoComplete="new-password"
          onChange={(event) => setPassword(event.target.value)}
          trailing={
            <button
              type="button"
              onClick={() => setShow((prev) => !prev)}
              aria-label={show ? 'Ocultar contraseña' : 'Mostrar contraseña'}
              className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md text-foreground-500 transition-colors hover:bg-background-100 hover:text-foreground-800"
            >
              <i className={`${show ? 'ri-eye-off-line' : 'ri-eye-line'} text-lg`}></i>
            </button>
          }
        />
        <Input
          label="Confirmar contraseña"
          name="confirm_password"
          type={show ? 'text' : 'password'}
          icon="ri-lock-line"
          value={confirm}
          autoComplete="new-password"
          onChange={(event) => setConfirm(event.target.value)}
        />
        {error && (
          <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-700">
            <span className="mt-0.5 flex h-4 w-4 items-center justify-center">
              <i className="ri-error-warning-line text-base"></i>
            </span>
            <span>{error}</span>
          </div>
        )}
        <p className="text-xs text-foreground-500">
          La contraseña actual no se muestra por seguridad. Al guardar, el usuario deberá usar la
          nueva contraseña en su próximo inicio de sesión.
        </p>
      </div>
    </Modal>
  );
}